'use client'

import type { MouseEvent } from 'react'
import { useCallback, useRef, useState } from 'react'

import { useRouter } from 'next/navigation'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Fade from '@mui/material/Fade'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Typography from '@mui/material/Typography'

import { Box, Skeleton } from '@mui/material'

import CustomButton from '@/components/CustomButton'
import showToastAlert from '@components/Toast'
import { useSettings } from '@core/hooks/useSettings'

const UserDropdown = ({ dataSession }: { dataSession: any }) => {
  const router = useRouter()
  const NAMA_USER = dataSession?.NAMA_USER

  const [open, setOpen] = useState(false)
  const [logout, setLogout] = useState(false)

  const anchorRef = useRef<HTMLDivElement>(null)

  const { settings } = useSettings()

  const handleDropdownOpen = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  const handleDropdownClose = useCallback(
    (event?: MouseEvent<HTMLLIElement> | (MouseEvent | TouchEvent), url?: string) => {
      if (url) {
        router.push(url)
      }

      if (anchorRef.current && anchorRef.current.contains(event?.target as HTMLElement)) {
        return
      }

      setOpen(false)
    },
    [router]
  )

  const handleUserLogout = useCallback(async () => {
    setLogout(true)

    try {
      // await signOut({ redirect: false } as const)
      sessionStorage.clear()
      window.location.href = '/login'
    } catch (error: unknown) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLogout(false)
    }
  }, [])

  return (
    <>
      <div className='flex flex-col items-center'>
        {NAMA_USER ? (
          <>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              component='div'
              onClick={handleDropdownOpen}
              ref={anchorRef}
              endIcon={<i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '3.5rem',
                width: { xs: '100%', sm: 'auto' },
                minWidth: '125px',
                maxWidth: '275px',
                height: '2.2rem',
                typography: 'body2',
                padding: '0 1rem',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main'
                }
              }}
            >
              {NAMA_USER}
            </Button>
          </>
        ) : (
          <Box sx={{ width: '6vw' }}>
            <Skeleton />
          </Box>
        )}
      </div>

      <Popper
        open={open}
        transition
        disablePortal
        placement='bottom-end'
        anchorEl={anchorRef.current}
        className='min-is-[240px] !mbs-4 z-[1]'
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-end' ? 'right top' : 'left top'
            }}
          >
            <Paper
              elevation={settings.skin === 'bordered' ? 0 : 8}
              {...(settings.skin === 'bordered' && { className: 'border' })}
            >
              <ClickAwayListener onClickAway={e => handleDropdownClose(e as MouseEvent | TouchEvent)}>
                <MenuList>
                  <div className='flex items-center plb-2 pli-4 gap-2' tabIndex={-1}>
                    <Avatar alt='John Doe' src='/images/avatars/1.png' />
                    <div className='flex items-start flex-col'>
                      <Typography variant='body2' className='font-medium' color='text.primary'>
                        {NAMA_USER}
                      </Typography>
                    </div>
                  </div>
                  <div className='flex items-center justify-center plb-1.5 pli-4'>
                    <CustomButton
                      endIcon={<i className='ri-logout-box-r-line' style={{ fontSize: '1.2rem' }} />}
                      type='button'
                      color='error'
                      loading={logout}
                      onClick={handleUserLogout}
                    >
                      Logout
                    </CustomButton>
                  </div>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default UserDropdown
